/**
 * Blocks access to paid content without active subscription.
 * Shows a paywall CTA instead of the content.
 */

import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lock, ArrowRight, CheckCircle2 } from 'lucide-react'
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'
import { useQuery } from '@tanstack/react-query'
import { getCoursePricing, formatPrice } from '@/lib/coursePricing'
import { getCourse, contentModules } from '@/lib/coursesRegistry'

interface Props {
  children: React.ReactNode
  /** If true, content is always accessible (e.g. free courses) */
  free?: boolean
}

export function MwSubscriptionGate({ children, free }: Props) {
  const { canBuyDirect } = usePlatformSettings()

  // El numero de modulos, del curso: si se anade uno, el texto lo dice
  const cursoPrincipal = getCourse('mentor-wallet')
  const totalModulos = cursoPrincipal ? contentModules(cursoPrincipal).length : 0

  const { data: pricing } = useQuery({
    queryKey: ['course-pricing', 'mentor-wallet'],
    queryFn: () => getCoursePricing('mentor-wallet'),
    staleTime: 10 * 60 * 1000,
  })
  const precio = pricing?.b2cPriceCents ?? null
  const { hasAccess, status } = useSubscriptionStatus()
  const { t: tMw } = useTranslation('mw')

  if (free || hasAccess) return <>{children}</>
  if (status === 'loading') return <div className="py-12 text-center"><div className="size-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto" /></div>

  return (
    <div className="py-12 flex flex-col items-center text-center" style={{ maxWidth: 'var(--mw-w-dialog)', margin: '0 auto', padding: '0 var(--mw-p-page)' }}>
      <div className="size-16 rounded-full flex items-center justify-center mb-6"
        style={{ background: '#EFF4FF', color: 'var(--mw-accent)' }}>
        <Lock className="size-7" aria-hidden />
      </div>

      <h2 style={{ fontFamily: 'var(--mw-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--mw-ink)', margin: '0 0 8px' }}>
        {canBuyDirect ? tMw('premium.lockTitle') : tMw('premium.lockB2bTitle')}
      </h2>

      <p style={{ fontSize: 14, color: 'var(--mw-ink-secondary)', lineHeight: 1.6, margin: '0 0 12px' }}>
        {canBuyDirect
          ? tMw('dashboard.noSubscriptionHint')
          : tMw('premium.lockB2bBody')}
      </p>

      {/* Lo que mas tranquiliza a quien ya habia empezado */}
      <p style={{ fontSize: 13, color: 'var(--mw-ink-muted)', lineHeight: 1.55, margin: '0 0 24px' }}>
        {tMw('premium.progressKept')}
      </p>

      {canBuyDirect && (
      <div className="w-full rounded-xl p-5 mb-6 text-left" style={{ background: '#F9FAFB', border: '1px solid var(--mw-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--mw-ink)', marginBottom: 12 }}>
          {tMw('premium.subscriberBenefits')}
        </div>
        {['benefit1', 'benefit2', 'benefit3', 'benefit4', 'benefit5', 'benefit6'].map(key => (
          <div key={key} className="flex items-start gap-2 mb-2" style={{ fontSize: 13, color: '#4B5563' }}>
            <CheckCircle2 className="size-4 shrink-0 mt-0.5" style={{ color: 'var(--mw-success)' }} aria-hidden />
            {tMw(`premium.${key}`, { n: totalModulos })}
          </div>
        ))}
      </div>
      )}

      {canBuyDirect && (
        <>
          <Link
            to="/comprar/mentor-wallet"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl no-underline"
            style={{ background: 'var(--mw-accent)', color: 'white', fontSize: 15, fontWeight: 600 }}>
            {tMw('premium.trialCta')}
            <ArrowRight className="size-4" aria-hidden />
          </Link>

          {precio !== null && (
            <p style={{ fontSize: 12, color: 'var(--mw-ink-muted)', marginTop: 12 }}>
              {formatPrice(precio)} al ano · {tMw('checkout.vatIncluded')}
            </p>
          )}
        </>
      )}
    </div>
  )
}
